import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';

import { FinnairCompletionStatus, FinnairOrder } from '@fcom/dapi/api/models';
import { ModalButtons } from '@fcom/ui-components/components/modal';
import { ButtonLayout, ButtonPosition, ButtonTheme, ButtonType } from '@fcom/ui-components/components/buttons';
import { LanguageService } from '@fcom/ui-translate/services/language.service';
import { RootPaths } from '@fcom/core/constants';
import { MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';
import { NotificationLayout, NotificationTheme } from '@fcom/ui-components/components/notifications';

import { CheckInService } from '../../../services/check-in.service';
import { FinnairPassengerItemWithApis } from '../interface/check-in.interface';

@Component({
  selector: 'fin-check-in-apis-route',
  templateUrl: 'check-in-apis-route.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckInApisRouteComponent implements OnInit, OnDestroy {
  readonly ModalButtons = ModalButtons;
  readonly ButtonType = ButtonType;
  readonly ButtonTheme = ButtonTheme;
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonLayout = ButtonLayout;
  readonly NotificationTheme = NotificationTheme;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly NotificationLayout = NotificationLayout;

  order$: Observable<FinnairOrder>;
  boundId$: Observable<string>;
  passengers$: Observable<FinnairPassengerItemWithApis[]>;
  canContinue$: Observable<boolean>;
  passengerInAgentReval$: Observable<boolean>;
  ctaOpenApisFormAction$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);
  isApisCompleted$: Observable<boolean>;

  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private languageService: LanguageService,
    private checkInService: CheckInService
  ) {}

  ngOnInit(): void {
    this.passengers$ = this.checkInService.getPassengersWithApis();
    this.passengerInAgentReval$ = this.passengers$.pipe(
      map((passengers) => passengers.some((pax) => pax.isInAgentReval))
    );
    this.order$ = this.checkInService.getOrder();
    this.boundId$ = this.checkInService.getBound().pipe(map((b) => b.id));
    this.isApisCompleted$ = this.checkInService.isApisCompleted();
    this.canContinue$ = combineLatest([this.order$, this.boundId$, this.passengers$]).pipe(
      map(([order, boundId, passengers]) => {
        const allPassengerIds = passengers.map(({ id }) => id);

        const segmentCheckInEligibilities =
          order.eligibilities?.checkIn?.find((e) => e.id === boundId)?.flightTravelerEligibility ?? {};

        const values = Object.values(segmentCheckInEligibilities);
        return (
          values.length > 0 &&
          values.every((eligibility) => {
            const allowedEligibilities = eligibility.filter((e) => e.isAllowedToAcceptCheckIn);
            return (
              allowedEligibilities.length > 0 &&
              allowedEligibilities.every((e) => allPassengerIds.every((paxId) => e.travelerIds.includes(paxId)))
            );
          })
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  continueToNextStep(): void {
    this.subscriptions.add(
      combineLatest([
        this.canContinue$,
        this.passengers$.pipe(
          map(
            (passengers) =>
              passengers.find((passenger) => passenger.apisCpr?.status !== FinnairCompletionStatus.COMPLETED)?.id
          )
        ),
        this.passengers$.pipe(map((passengers) => passengers.find((passenger) => !!passenger.isInAgentReval)?.id)),
        this.checkInService.isExitRowSeatConfirmationRequired(),
      ])
        .pipe(take(1))
        .subscribe(
          ([canContinue, passengerIdWithMissingDetails, passengerWithAgentReval, exitRowSeatsConfirmationRequired]) => {
            if (passengerWithAgentReval) {
              this.ctaOpenApisFormAction$.next(passengerWithAgentReval);
            } else if (passengerIdWithMissingDetails) {
              this.ctaOpenApisFormAction$.next(passengerIdWithMissingDetails);
            } else if (exitRowSeatsConfirmationRequired && canContinue) {
              this.checkInService.setShowExitRowSeatsNotification(true);
            } else if (canContinue) {
              this.checkInService.triggerNextCheckInStep();
            }
          }
        )
    );
  }

  goToMmb(): void {
    this.router.navigateByUrl(
      `/${this.languageService.langValue}/${RootPaths.MANAGE_BOOKING_ROOT}/${MANAGE_BOOKING_STEPS.VIEW.path}`
    );
  }
}
